<template>
  <layout-admin current="referrals">
    <div class="referrals">
      <div class="panel">
        <div class="panel__head">
          <h1>{{ referral.name }}</h1>
          <span class="mono">{{ referral.code }}</span>
        </div>
        <form class="form is-compact" @submit.prevent="save">
          <p v-for="(error, key) in errors" :key="key" class="form__error">{{ message(error) }}</p>
          <div class="form__row">
            <label>
              Name
              <input v-model="form.name" type="text" maxlength="120" required>
            </label>
            <label>
              Landing path
              <input v-model="form.landing_path" type="text" maxlength="200" spellcheck="false" required>
            </label>
          </div>
          <div class="form__line">
            <label>
              Notes
              <textarea v-model="form.notes" maxlength="2000" rows="1"></textarea>
            </label>
            <label class="form__check">
              <input v-model="form.active" type="checkbox">
              Active
            </label>
            <button class="btn is-view" type="submit" :disabled="saving">Save</button>
            <Link :href="route('admin.referrals.index')" class="btn">Back</Link>
          </div>
          <div class="links">
            <label>
              Short link
              <span class="links__line">
                <input type="text" readonly spellcheck="false" :value="referral.short_url" @focus="selectUrl">
                <button type="button" class="btn" @click="copy(referral.short_url)">
                  {{ copied === referral.short_url ? 'Copied' : 'Copy' }}
                </button>
              </span>
            </label>
            <label>
              Page link
              <span class="links__line">
                <input type="text" readonly spellcheck="false" :value="referral.deep_url" @focus="selectUrl">
                <button type="button" class="btn" @click="copy(referral.deep_url)">
                  {{ copied === referral.deep_url ? 'Copied' : 'Copy' }}
                </button>
              </span>
            </label>
          </div>
        </form>
      </div>

      <div class="panel">
        <div class="panel__head">
          <h1>Last 30 days</h1>
        </div>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Day</th>
                <th class="num">Clicks</th>
                <th class="num">Visitors</th>
                <th class="num">Leads</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="day in days" :key="day.date">
                <td>{{ day.date }}</td>
                <td class="num">{{ day.clicks }}</td>
                <td class="num">{{ day.uniques }}</td>
                <td class="num">{{ day.leads }}</td>
              </tr>
            </tbody>
          </table>

          <table>
            <thead>
              <tr>
                <th>Latest landings</th>
                <th>Path</th>
                <th>Referrer</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!landings.length">
                <td colspan="3" class="muted">No landings yet.</td>
              </tr>
              <tr v-for="(landing, index) in landings" :key="index">
                <td>{{ landing.at }}</td>
                <td class="mono">{{ landing.path }}</td>
                <td class="mono">{{ landing.host || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </layout-admin>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3';
import LayoutAdmin from '@/Layouts/Admin.vue';

export default {
  components: {
    LayoutAdmin,
    Link
  },

  props: {
    referral: { type: Object, required: true },
    days: { type: Array, required: true },
    landings: { type: Array, required: true }
  },

  data() {
    return {
      saving: false,
      copied: '',
      form: {
        name: this.referral.name,
        landing_path: this.referral.landing_path,
        notes: this.referral.notes,
        active: this.referral.active
      }
    };
  },

  computed: {
    errors() {
      return this.$page.props.errors || {};
    }
  },

  methods: {
    message(error) {
      return Array.isArray(error) ? error[0] : error;
    },

    save() {
      if (this.saving) {
        return;
      }

      this.saving = true;
      this.$inertia.put(this.route('admin.referrals.update', this.referral.id), this.form, {
        preserveScroll: true,
        onFinish: () => {
          this.saving = false;
        }
      });
    },

    selectUrl(event) {
      event.target.select();
    },

    copy(url) {
      const done = () => {
        this.copied = url;
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done);

        return;
      }

      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      done();
    }
  }
};
</script>

<style lang="scss" scoped>
.referrals {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
}

.panel:first-child {
  flex: none;
}

.panel:last-child {
  flex: 1;
}

.form__line {
  align-items: flex-end;
  display: flex;
  gap: 0.75rem;

  label:first-child {
    flex: 1;
    min-width: 0;
  }
}

.links {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr 1fr;
}

.links__line {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  min-width: 0;

  input {
    flex: 1;
    font-family: ui-monospace, Consolas, monospace;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.btn {
  align-items: center;
  display: inline-flex;
  text-decoration: none;
}
</style>
